import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Link } from 'react-router-dom';
import { Grid, List, ArrowRight, Filter } from 'lucide-react';
import { ImageWithFallback } from "../components/figma/ImageWithFallback";
import { caseStudies } from "../data/portfolio";

type ViewMode = 'grid' | 'list';
type FilterType = 'all' | 'work' | 'research';

export default function CaseStudies() {
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [filter, setFilter] = useState<FilterType>('all');

  const filteredStudies = caseStudies.filter(study => 
    filter === 'all' || study.category === filter
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen pt-32 md:pt-40 pb-20 px-4"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl md:text-5xl text-foreground mb-6">
            Case Studies
          </h1>
          <p className="text-muted-foreground text-lg max-w-3xl mx-auto leading-relaxed">
            A deep dive into my design process, from initial research to final implementation. 
            Each project represents a unique challenge solved through user-centered design.
          </p>
        </motion.div>

        {/* Filters and View Toggle */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex flex-col sm:flex-row items-center justify-between mb-12 space-y-4 sm:space-y-0"
        >
          {/* Filter Buttons */}
          <div className="flex items-center space-x-2 bg-muted/50 p-1 rounded-xl">
            {[
              { key: 'all', label: 'All Projects' },
              { key: 'work', label: 'Client Work' },
              { key: 'research', label: 'Research' }
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setFilter(key as FilterType)}
                className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${
                  filter === key
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* View Toggle */}
          <div className="flex items-center space-x-2 bg-muted/50 p-1 rounded-xl">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg transition-all ${
                viewMode === 'grid'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Grid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg transition-all ${
                viewMode === 'list'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </motion.div>

        {/* Case Studies Grid/List */}
        <AnimatePresence mode="wait">
          <motion.div
            key={`${viewMode}-${filter}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
            className={
              viewMode === 'grid'
                ? 'grid md:grid-cols-2 lg:grid-cols-3 gap-8'
                : 'space-y-6'
            }
          >
            {filteredStudies.map((study, index) => (
              <motion.div
                key={study.slug}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group"
              >
                <Link to={`/case-studies/${study.slug}`} className="block">
                  {viewMode === 'grid' ? (
                    <div className="bg-card backdrop-blur-xl border border-border rounded-2xl overflow-hidden hover:shadow-2xl hover:shadow-purple-500/10 transition-all duration-500 group-hover:-translate-y-2">
                      <div className="aspect-[4/3] overflow-hidden">
                        <ImageWithFallback
                          src={study.image}
                          alt={study.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      
                      <div className="p-6">
                        <div className="flex items-center space-x-2 mb-4">
                          <span className={`px-3 py-1 rounded-full text-xs ${
                            study.category === 'work' 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {study.category === 'work' ? 'Client Work' : 'Research'}
                          </span>
                          <span className="text-muted-foreground text-sm">{study.year}</span>
                        </div>
                        
                        <h3 className="text-lg mb-2 group-hover:text-purple-600 transition-colors">
                          {study.title}
                        </h3>
                        
                        <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                          {study.subtitle}
                        </p>
                        
                        <div className="flex flex-wrap gap-2 mb-4">
                          {study.tags.slice(0, 2).map((tag) => (
                            <span
                              key={tag}
                              className="text-xs bg-muted px-2 py-1 rounded-md text-muted-foreground"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                        
                        <div className="flex items-center space-x-2 text-purple-600">
                          <span className="text-sm">Read case study</span>
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-card backdrop-blur-xl border border-border rounded-2xl p-6 hover:shadow-lg hover:shadow-purple-500/5 transition-all duration-300 group-hover:border-purple-200">
                      <div className="flex flex-col md:flex-row md:items-center md:space-x-6 space-y-4 md:space-y-0">
                        <div className="md:w-48 md:h-32 flex-shrink-0">
                          <div className="aspect-[3/2] md:aspect-auto md:h-full overflow-hidden rounded-xl">
                            <ImageWithFallback
                              src={study.image}
                              alt={study.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          </div>
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <span className={`px-3 py-1 rounded-full text-xs ${
                              study.category === 'work' 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-blue-100 text-blue-700'
                            }`}>
                              {study.category === 'work' ? 'Client Work' : 'Research'}
                            </span>
                            <span className="text-muted-foreground text-sm">{study.year}</span>
                          </div>
                          
                          <h3 className="text-xl mb-2 group-hover:text-purple-600 transition-colors">
                            {study.title}
                          </h3>
                          
                          <p className="text-muted-foreground mb-4">
                            {study.subtitle}
                          </p>
                          
                          <div className="flex flex-wrap gap-2 mb-4">
                            {study.tags.map((tag) => (
                              <span
                                key={tag}
                                className="text-xs bg-muted px-2 py-1 rounded-md text-muted-foreground"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                          
                          <div className="flex items-center space-x-2 text-purple-600">
                            <span className="text-sm">Read case study</span>
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>

        {filteredStudies.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20"
          >
            <Filter className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg text-foreground mb-2">No case studies found</h3>
            <p className="text-muted-foreground">Try adjusting your filter to see more results.</p>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}
